import { useEffect, useState } from 'react';
import { Download, Share } from 'lucide-react';

/**
 * "Install app" button for the PWA. Uses the browser's `beforeinstallprompt`
 * event (Chrome/Edge/Android) to trigger the native install dialog. iOS Safari
 * doesn't support that event, so we show a short "Add to Home Screen" hint
 * instead. Hidden entirely once the app is already running standalone.
 *
 * The event can fire before any component mounts, so we capture it at module
 * load and re-broadcast via a custom event the component subscribes to.
 *
 * Note: the service worker (and therefore installability) is only active in a
 * production build / preview — not `vite dev` (devOptions.enabled = false). On
 * desktop Firefox/Safari, which don't support programmatic install, the button
 * stays hidden.
 */

let deferredPrompt: any = null;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault(); // stop the mini-infobar; we drive install from our button
    deferredPrompt = e;
    window.dispatchEvent(new Event('pwa-installable'));
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    window.dispatchEvent(new Event('pwa-installed'));
  });
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches
    || (navigator as any).standalone === true;
}

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !(window as any).MSStream;
}

export function InstallAppButton({ className = '' }: { className?: string }) {
  const [canInstall, setCanInstall] = useState<boolean>(!!deferredPrompt);
  const [installed, setInstalled] = useState<boolean>(isStandalone());
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    const onInstallable = () => setCanInstall(true);
    const onInstalled = () => { setInstalled(true); setCanInstall(false); };
    window.addEventListener('pwa-installable', onInstallable);
    window.addEventListener('pwa-installed', onInstalled);
    return () => {
      window.removeEventListener('pwa-installable', onInstallable);
      window.removeEventListener('pwa-installed', onInstalled);
    };
  }, []);

  // Already installed → nothing to offer.
  if (installed) return null;

  const ios = isIOS();
  // Show when we have a native prompt (Android/desktop Chromium) or on iOS
  // (manual instructions). Otherwise the browser can't install — stay hidden.
  if (!canInstall && !ios) return null;

  const handleClick = async () => {
    if (ios) {
      setShowIosHelp((v) => !v);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch { /* user dismissed */ }
    deferredPrompt = null;
    setCanInstall(false);
  };

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={handleClick}
        className={`btn w-full border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center justify-center gap-1.5 ${className}`}
      >
        <Download size={15} /> Install app
      </button>
      {ios && showIosHelp && (
        <div className="text-[11px] leading-snug text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          On iPhone/iPad: tap the <Share size={11} className="inline -mt-0.5" /> Share button in
          Safari, then choose <b>“Add to Home Screen.”</b>
        </div>
      )}
    </div>
  );
}
